import { useState, useCallback, useEffect } from "react"
import Papa, { ParseConfig } from "papaparse"
import { useDropzone } from "react-dropzone"
import { Flex } from "../Flex"
import { Text } from "../Typography"
import { Button } from "../Button"
import { UploadWrapper, DragContainer, Status } from "./uploadCSVStyles"
import { StyleProps } from "../styles"

const TEST_ID_PREFIX = `upload-csv-`
export const TEST_ID = {
  INPUT: `${TEST_ID_PREFIX}input`,
  RESET: `${TEST_ID_PREFIX}reset`,
  UPLOAD: `${TEST_ID_PREFIX}upload`,
}

/**
 * Uses the booleans returned by useDropzone to decide on status to apply to
 * stitches styled div
 */
const getStatus = (
  isDragAccept: boolean,
  isDragReject: boolean,
  isDragActive: boolean,
  moreThanOneFile: boolean
): Status | undefined => {
  if (isDragActive) return undefined
  if (isDragAccept) return `accept`
  if (isDragReject || moreThanOneFile) return `reject`
  return undefined
}

export const DEFAULT_TEXT = `Drag and drop your file here, or click to select your file`
/**
 * Decides on the text to be displayed in the drop box
 * @param isDragActive // true when files are being dragged into the box
 * @param fileError // true when the files dragged into the box could not be processed
 * @param moreThanOneFile // whether more than one file has been uploaded
 * @param hasPendingFile // if there is a pending file
 */
const getDropZoneText = (
  isDragActive: boolean,
  fileError: boolean,
  moreThanOneFile: boolean,
  hasPendingFile: boolean,
  fileContainsErrors: ReadonlyArray<string>
): string => {
  if (isDragActive) return `Drop your file here ...`
  if (fileError && fileContainsErrors.length > 0) return fileContainsErrors.join(`\r\n`)
  if (fileError) return `Please check your file is a valid csv file`
  if (moreThanOneFile) return `Please select exactly one CSV file`
  if (hasPendingFile) return `File ready to upload`
  return DEFAULT_TEXT
}

interface Config extends Omit<ParseConfig, "complete"> {
  encoding?: string
}

/**
 * Override some of the defaults used by papaparse for config
 * @param config
 * @returns
 */
const overrideDefaultConfig = (config?: Config): Config => ({
  ...config,
  header: config?.header !== undefined ? config.header : true,
  encoding: config?.encoding || `utf-8`,
  skipEmptyLines: config?.skipEmptyLines ?? true,
})

export interface Props {
  parseConfig?: Config
  uploadData: (data: unknown[]) => void
  cancelButton?: {
    action: () => void
    disabled?: boolean
    text: string
  }
}

const buttonWrapStyle = (props: StyleProps) => ({
  marginTop: props.theme.spacing(10),
  padding: props.theme.spacing(0, 5),
})

export const UploadCSV = ({ parseConfig, cancelButton, uploadData }: Props) => {
  const [status, setStatus] = useState<Status | undefined>()
  const [text, setText] = useState(DEFAULT_TEXT)
  const [fileError, setFileError] = useState(false)
  const [moreThanOneFile, setMoreThanOneFile] = useState(false)
  const [pendingData, setPendingData] = useState<unknown[]>()
  const [fileContainsErrors, setFileContainsErrors] = useState<ReadonlyArray<string>>([])

  const onUploadClick = () => {
    if (!pendingData) return
    uploadData(pendingData)
    setPendingData(undefined)
  }

  const onResetClick = () => {
    setPendingData(undefined)
  }

  /**
   * Parses the file as csv and sets it into local state
   * @param csvString
   */
  const parseFile = useCallback(
    (csvString: string) => {
      Papa.parse(csvString, {
        ...overrideDefaultConfig(parseConfig),
        complete: (results: Papa.ParseResult<unknown>) => {
          if (results.errors?.length) {
            setFileError(true)
            setFileContainsErrors(results.errors.map(({ message }) => message))
            return
          }

          setPendingData(results.data)
        },
      })
    },
    [parseConfig]
  )

  /**
   * The handler for useDropzone when files are added by user
   */
  const onDrop = useCallback(
    (acceptedFiles: File[]): void => {
      // onDrop reset previous errors - they will be re-evaluated.
      setFileError(false)
      setFileContainsErrors([])

      if (!acceptedFiles.length) return setFileError(true)
      if (acceptedFiles.length > 1) return setMoreThanOneFile(true)

      const file = acceptedFiles[0] as unknown
      return parseFile(file as string)
    },
    [parseFile]
  )

  const { getRootProps, getInputProps, isDragActive, isDragAccept } = useDropzone({
    onDrop,
    accept: `.csv`,
  })

  useEffect(() => {
    setText(getDropZoneText(isDragActive, fileError, moreThanOneFile, !!pendingData, fileContainsErrors))
    setStatus(getStatus(isDragActive, fileError, fileError, moreThanOneFile))
  }, [isDragActive, isDragAccept, fileError, moreThanOneFile, pendingData, fileContainsErrors])

  return (
    <UploadWrapper>
      <DragContainer {...getRootProps()} status={status as Status}>
        <input data-testid={TEST_ID.INPUT} {...getInputProps()} />
        <Text>{text}</Text>
      </DragContainer>
      <Flex justifyContent="space-between" css={buttonWrapStyle}>
        {cancelButton && (
          <Button variant="outline" disabled={cancelButton.disabled} onClick={() => cancelButton.action()}>
            {cancelButton.text}
          </Button>
        )}
        <Button data-testid={TEST_ID.RESET} variant="outline" disabled={!pendingData} onClick={onResetClick}>
          Reset
        </Button>
        <Button data-testid={TEST_ID.UPLOAD} disabled={!pendingData} onClick={onUploadClick}>
          Upload
        </Button>
      </Flex>
    </UploadWrapper>
  )
}

UploadCSV.defaultProps = {
  parseConfig: {},
  cancelButton: undefined,
}
